import { JSX } from 'react'
import { TemplateAttributeTypes } from '@/lib/api/client'
import { Position } from '@/services/hooks/template_attributes'
import { Virtualizer } from '@tanstack/react-virtual'
import { create } from 'zustand'

type SelectedElementData = {
  documentPageId: string
  id: string
  isRemovable?: boolean
} | null
type NewElementData = {
  dataType: TemplateAttributeTypes
  Component: (props: any) => JSX.Element
  attributes: {
    [key: string]: any
  }
  documentPageId?: string
} | null
type Align = 'start' | 'center' | 'end' | 'auto'
type ScrollOpts = { align?: Align; behavior?: ScrollBehavior; offset?: number }
interface DocumentState {
  zoom: number
  defaultZoom: number
  commentsEnabled: boolean
  pageTemplatesEnabled: boolean
  selectedElement: SelectedElementData
  currentPageInView: string
  nImagesLoaded: number
  documentContainerLoaded: boolean
  draggableAnnotationParentRefs: {
    [templateId: string]: {
      templateId: string
      parentRef: HTMLDivElement | null
    }
  }
  isAddingElement: boolean
  canDropElement: boolean
  menuRefPosition: Position
  triggerLocation: Position
  newElementData: NewElementData
  virtualizer: Virtualizer<any, any> | null
  pageIds: string[]
  idToIndex: Map<string, number>
  pending?: { id: string; opts?: ScrollOpts }
  setMenuRefPosition: (position: Position) => void
  setCanDropElement: (canDropElement: boolean) => void
  startAddingElement: (payload: {
    newElementData: NewElementData
    triggerLocation: Position
  }) => void
  setLinePoint: (payload: { position: Position; type: 'start' | 'end' }) => void
  clearNewElementData: () => void
  onImageLoaded: () => void
  resetImagesLoaded: () => void
  getState: () => DocumentState
  setState: (state: Partial<DocumentState>) => void
  setZoom: (zoom: number) => void
  setDefaultZoom: (defaultZoom: number) => void
  setCommentsEnabled: (commentsEnabled: boolean) => void
  setSelectedElement: (elementData: SelectedElementData) => void
  setCurrentPageInView: (currentPageInViewId: string) => void
  setDraggableAnnotationParentRef: (
    templateId: string,
    parentRef: HTMLDivElement | null,
  ) => void
  getDraggableAnnotationParentRef: (templateId: string) => HTMLDivElement | null
  registerVirtualizer: (v: Virtualizer<any, any> | null) => void
  setPageOrder: (ids: string[]) => void
  scrollToPageId: (id: string, opts?: ScrollOpts) => void
}

export const MAIN_DOCUMENT_CONTAINER_TOP_PADDING = 65

export const useDocumentState = create<DocumentState>((set, get) => ({
  zoom: 100,
  defaultZoom: 0,
  commentsEnabled: false,
  pageTemplatesEnabled: false,
  selectedElement: null,
  currentPageInView: '',
  nImagesLoaded: 0,
  documentContainerLoaded: false,
  isAddingElement: false,
  canDropElement: false,
  menuRefPosition: { x: 0, y: 0 },
  triggerLocation: { x: 0, y: 0 },
  newElementData: null,
  virtualizer: null,
  pageIds: [],
  idToIndex: new Map(),
  setMenuRefPosition: (position) => set({ menuRefPosition: position }),
  setCanDropElement: (canDropElement) => set({ canDropElement }),
  startAddingElement: ({ newElementData, triggerLocation }) =>
    set({
      isAddingElement: true,
      newElementData,
      triggerLocation,
    }),
  setLinePoint: ({ position, type }) => {
    set((state) => ({
      newElementData: {
        ...state.newElementData,
        attributes: {
          ...state.newElementData?.attributes,
          [`${type}Point`]: position,
        },
      } as NewElementData,
    }))
  },
  clearNewElementData: () =>
    set({
      newElementData: null,
      isAddingElement: false,
      triggerLocation: { x: 0, y: 0 },
    }),
  resetImagesLoaded: () => {
    set(() => ({
      nImagesLoaded: 0,
    }))
  },
  onImageLoaded: () => {
    set((state) => ({
      nImagesLoaded: state.nImagesLoaded + 1,
    }))
  },
  draggableAnnotationParentRefs: {},

  getState: () => get(),
  setState: (state: Partial<DocumentState>) => {
    set(state)
  },
  setZoom: (zoom) => set({ zoom }),
  setDefaultZoom: (defaultZoom) =>
    set((store) => ({
      defaultZoom,
      zoom: store.zoom + defaultZoom,
    })),
  setCommentsEnabled: (commentsEnabled) => set({ commentsEnabled }),
  setSelectedElement: (elementData) =>
    set({
      selectedElement: {
        isRemovable: true,
        ...elementData,
      } as any,
    }),
  setCurrentPageInView: (currentPageInView) => set({ currentPageInView }),
  setDraggableAnnotationParentRef: (
    templateId: string,
    parentRef: HTMLDivElement | null,
  ) =>
    set((state) => ({
      ...state,
      draggableAnnotationParentRefs: {
        ...state.draggableAnnotationParentRefs,
        [templateId]: {
          templateId,
          parentRef,
        },
      },
    })),
  getDraggableAnnotationParentRef: (templateId: string) =>
    get().draggableAnnotationParentRefs[templateId]?.parentRef || null,
  registerVirtualizer: (v) => {
    set({ virtualizer: v })
    const pending = get().pending
    if (v && pending) {
      set({ pending: undefined })
      get().scrollToPageId(pending.id, pending.opts)
    }
  },

  setPageOrder: (ids) =>
    set({
      pageIds: ids,
      idToIndex: new Map(ids.map((id, i) => [id, i])),
    }),

  scrollToPageId: (id, opts) => {
    const v = get().virtualizer
    const idx = get().idToIndex.get(id)
    if (!v) {
      set({ pending: { id, opts } })
      return
    }
    if (idx == null) return

    const align = opts?.align ?? 'start'
    const behavior = opts?.behavior ?? 'auto'
    const offset = opts?.offset ?? 0

    v.measure()
    v.scrollToIndex(idx, { align })

    if (offset !== 0) {
      const el = v.scrollElement
      if (el instanceof HTMLElement) {
        requestAnimationFrame(() => {
          el.scrollTo({ top: el.scrollTop + offset, behavior })
        })
      }
    }
  },
}))
